import { Controller } from "@hotwired/stimulus"

const STATUS_CLASSES = [
  "text-[var(--ds-text-secondary)]",
  "text-[var(--ds-statuses-success)]",
  "text-[var(--ds-statuses-error)]"
]

export default class extends Controller {
  static targets = [
    "firstName",
    "lastName",
    "email",
    "password",
    "confirmation",
    "submit",
    "lengthRequirement",
    "caseRequirement",
    "numberRequirement",
    "nameRequirement",
    "requirementsStatus"
  ]

  static values = {
    minLength: { type: Number, default: 12 }
  }

  connect() {
    this.boundSync = () => this.sync()
    this.sync()
    window.addEventListener("pageshow", this.boundSync)
    // Password managers may fill after connect without firing input.
    requestAnimationFrame(() => this.sync())
  }

  disconnect() {
    if (this.boundSync) {
      window.removeEventListener("pageshow", this.boundSync)
    }
  }

  sync() {
    if (!this.hasPasswordTarget) return

    const checks = this.passwordChecks()
    this.updateRequirementStyles(checks)
    this.announceRequirements(checks)
    this.syncConfirmationValidity()

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !this.formReady(checks)
    }
  }

  formReady(checks) {
    return (
      this.fieldFilled(this.firstNameTarget) &&
      this.fieldFilled(this.lastNameTarget) &&
      this.fieldFilled(this.emailTarget) &&
      this.emailTarget.checkValidity() &&
      this.passwordRequirementsMet(checks) &&
      this.confirmationMatches()
    )
  }

  passwordRequirementsMet(checks) {
    return checks.length && checks.mixedCase && checks.number && checks.noPersonalInfo
  }

  passwordChecks() {
    const password = this.passwordTarget.value

    return {
      length: password.length >= this.minLengthValue,
      mixedCase: /[A-Z]/.test(password) && /[a-z]/.test(password),
      number: /\d/.test(password),
      noPersonalInfo: this.passwordOmitsPersonalInfo(password)
    }
  }

  passwordOmitsPersonalInfo(password) {
    if (!password) return true

    const lowered = password.toLowerCase()
    const parts = [
      this.hasFirstNameTarget ? this.firstNameTarget.value : null,
      this.hasLastNameTarget ? this.lastNameTarget.value : null,
      this.hasEmailTarget ? this.emailTarget.value : null
    ].map((part) => (part || "").trim().toLowerCase()).filter(Boolean)

    return parts.every((value) => !lowered.includes(value))
  }

  confirmationMatches() {
    if (!this.hasConfirmationTarget) return true

    const confirmation = this.confirmationTarget.value
    return (
      confirmation !== "" && confirmation === this.passwordTarget.value
    )
  }

  syncConfirmationValidity() {
    if (!this.hasConfirmationTarget) return

    const mismatch =
      this.confirmationTarget.value !== "" &&
      this.confirmationTarget.value !== this.passwordTarget.value

    this.confirmationTarget.setCustomValidity(
      mismatch ? "Passwords do not match" : ""
    )
    this.confirmationTarget.setAttribute("aria-invalid", mismatch ? "true" : "false")
  }

  updateRequirementStyles(checks) {
    const passwordPresent = this.passwordTarget.value !== ""

    if (this.hasLengthRequirementTarget) {
      this.setRequirementStyle(this.lengthRequirementTarget, checks.length, passwordPresent)
    }
    if (this.hasCaseRequirementTarget) {
      this.setRequirementStyle(this.caseRequirementTarget, checks.mixedCase, passwordPresent)
    }
    if (this.hasNumberRequirementTarget) {
      this.setRequirementStyle(this.numberRequirementTarget, checks.number, passwordPresent)
    }
    if (this.hasNameRequirementTarget) {
      this.setRequirementStyle(
        this.nameRequirementTarget,
        checks.noPersonalInfo,
        passwordPresent
      )
    }
  }

  setRequirementStyle(element, met, passwordPresent) {
    if (!element) return

    element.classList.remove(...STATUS_CLASSES)

    const status = element.querySelector("[data-requirement-status]")
    if (status) {
      status.textContent = passwordPresent ? (met ? "Met: " : "Not met: ") : ""
    }

    if (!passwordPresent) {
      element.classList.add("text-[var(--ds-text-secondary)]")
      return
    }

    element.classList.add(
      met
        ? "text-[var(--ds-statuses-success)]"
        : "text-[var(--ds-statuses-error)]"
    )
  }

  announceRequirements(checks) {
    if (!this.hasRequirementsStatusTarget) return

    const passwordPresent = this.passwordTarget.value !== ""
    if (!passwordPresent) {
      this.requirementsStatusTarget.textContent = ""
      this.lastRequirementsAnnouncement = ""
      return
    }

    const allMet = this.passwordRequirementsMet(checks)
    const message = allMet
      ? "All password requirements met"
      : "Password does not meet all requirements"

    if (message === this.lastRequirementsAnnouncement) return

    this.lastRequirementsAnnouncement = message
    this.requirementsStatusTarget.textContent = message
  }

  fieldFilled(element) {
    return !!element && element.value.trim() !== ""
  }
}
